<template>
  <Card
    :title="planName"
    :status="cardStatusColor"
    :statusText="cardStatusText"
    :disabled="isCardDisabled"
    class="SubscriptionDetailsCard"
    :class="{
      'is-disabled': isCardDisabled,
    }"
  >
    <template #header-action>
      <DropdownMenu
        v-if="!isCardDisabled"
        class="SubscriptionDetailsCard-actions"
        :title="t('ui.subscription.details.dropdown.label')"
        :items="subscriptionCardMenuItems"
      >
        <template #trigger>
          <ActionButton
            v-if="isTabletOrDesktop"
            class="SubscriptionDetailsCard-actionsTrigger"
            variant="tertiary"
            rightIcon="dots-vertical"
          >
            {{ t('ui.subscription.details.dropdown.label') }}
          </ActionButton>

          <IconButton
            v-else
            class="SubscriptionDetailsCard-actionsTrigger"
            icon="dots-vertical"
            size="md"
            :ariaLabel="t('ui.subscription.details.dropdown.label')"
          />
        </template>
      </DropdownMenu>
    </template>

    <div v-if="hasNotification" class="SubscriptionDetailsCard-row">
      <div class="SubscriptionDetailsCard-column">
        <Status
          :status="notification?.status"
          persistent
        >
          {{ t(notification!.text) }}
        </Status>
      </div>
    </div>

    <div class="SubscriptionDetailsCard-row">
      <div class="SubscriptionDetailsCard-column">
        <!-- QuotaDisplay -->
        <QuotaDisplay
          v-if="subscription.quota"
          :quota="subscription.quota"
          :tripType="subscription.plan.product?.tripType"
        />
      </div>

      <div class="SubscriptionDetailsCard-column">
        <SubscriptionAddonList
          v-if="hasAddons"
          :addons="subscription.plan.addons || []"
        />
      </div>
    </div>

    <div class="SubscriptionDetailsCard-row">
      <div class="SubscriptionDetailsCard-column">
        <PlanBenefits
          :benefits="characteristics"
          mode="detailed"
        />
      </div>
    </div>

    <p v-if="isCancellationRequested" id="subscription-cancellation-disclaimer" class="SubscriptionDetailsCard-disclaimer">
      {{ t('ui.subscription.details.cancelationRequested.disclaimer', { cancellationEffectiveDate: effectiveCancellationDateFormatted }) }}
    </p>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useResponsive, useDateFormat, useSubscription, useSubscriptionStatus } from '@cvo/ui/composables';
import {
  Card,
  DropdownMenu,
  ActionButton,
  IconButton,
  QuotaDisplay,
  SubscriptionAddonList,
  PlanBenefits,
  Status,
} from '@cvo/ui/components';
import type { DropdownMenuItem } from '@cvo/ui/components';
import type { SubscriptionDisplayStatus } from '@cvo/domain/api/models/bff/Subscription';
import { type SubscriptionCardProps, notificationMap } from './SubscriptionDetailsCard.model';

const props = withDefaults(defineProps<SubscriptionCardProps>(), {
  showPriceTooltip: true,
});
const emit = defineEmits<{
  (e: 'cancel-plan'): void;
}>();

const { t } = useI18n();
const { isTabletOrDesktop } = useResponsive();
const { cancellationDate, isCancelling } = useSubscription(() => props.subscription);
const { cardStatusColor, cardStatusText, isCardDisabled } = useSubscriptionStatus(() => props.subscription);

const effectiveCancellationDateFormatted = useDateFormat(cancellationDate, { dateFormat: 'short', fallback: '-' });

// ===== Card status and menu ===== //

const statusWithNotification: SubscriptionDisplayStatus[] = ['SUSPENDED', 'OVERDUE'];
const hasNotification = computed(() => statusWithNotification.includes(props.subscription.displayStatus));
const notification = computed(() => notificationMap[props.subscription.displayStatus] ?? null);

const isCancellationRequested = computed(() => props.subscription.cancellation?.status === 'PENDING');
const isCancellationEnabled = computed(() => {
  return props.subscription.cancellationEligibility.length > 0 && !isCancelling.value;
});

const subscriptionCardMenuItems = computed<DropdownMenuItem[]>(() => [
  {
    id: 'change-plan',
    icon: 'cancel',
    label: t('ui.subscription.details.dropdown.cancelPlan'),
    action: () => emit('cancel-plan'),
    disabled: !isCancellationEnabled.value,
  },
]);

// ===== Card data ===== //

const planName = computed(() => (props.subscription.plan ? t(`custom.plans.${props.subscription.plan.code}.title`) : '—'));
const hasAddons = computed(() => !!props.subscription.plan.addons?.length);

</script>

<style lang="scss">
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .SubscriptionDetailsCard {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-6;
    padding: vars.$dimensions-d-5;
    position: relative;
    border-radius: vars.$organisms-card-contentcard-subscription-details-border-radius;

    &.is-disabled {
      .QuotaDisplay,
      .SubscriptionAddonList,
      .PlanBenefits {
        opacity: 0.5;
        filter: grayscale(1);
      }
    }

    &-actionsTrigger {
      padding-inline: 0;
    }

    &-row {
      display: flex;
      gap: vars.$dimensions-d-5;

      @include responsive.mobile {
        flex-direction: column;
      }
    }

    &-column {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
    }

    &-disclaimer {
      color: vars.$organisms-card-contentcard-subscription-details-default-disclaimer-color;
      @include text.text(xs);
    }
  }
}
</style>
